import { KeyboardArrowDown } from "@mui/icons-material"
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp"
import { Typography, styled } from "@mui/material"
import { useMemo, useState } from "react"
import { extraColors } from "../../../utils/theme"
import {
  useListAllTrials,
  type OrganizationUsersTrials,
} from "../hooks/useManageAssetServiceUsers"
import { UserWithTrial } from "./AssetServiceUsers"

const PageContainer = styled("div")({
  maxWidth: 1000,
  width: "100%",
})

const TitleRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  cursor: "pointer",
  alignItems: "center",
})

const KeyboardArrowDownStyled = styled(KeyboardArrowDown)({
  color: extraColors.status.navyLight,
  transform: "scale(1.05)",
  transition: "transform 200ms ease",
  "&:hover": {
    transform: "scale(1.2)",
  },
})

const KeyboardArrowUpStyled = styled(KeyboardArrowUp)({
  color: extraColors.status.navyLight,
  transform: "scale(1.05)",
  transition: "transform 200ms ease",
  "&:hover": {
    transform: "scale(1.2)",
  },
})

const UsersContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  marginTop: 18,
  gap: 20,
})

const OrgContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  padding: "30px 40px",
  borderRadius: 6,
  border: `2px solid ${extraColors.status.navyLightTransparent}`,
})

const OrgsContainer = styled("div")({
  display: "grid",
  gridAutoFlow: "row",
  gap: 20,
  marginTop: 20,
})

export const AssetServiceOrganizations: React.FC = () => {
  const { data: trialData } = useListAllTrials()

  return (
    <PageContainer>
      <Typography
        variant="h4"
        sx={{
          padding: "12px",
          borderRadius: "6px",
          color: extraColors.white,
          background: extraColors.status.navyDark,
        }}
      >
        Asset Service Organizations
      </Typography>
      <OrgsContainer>
        {trialData?.organizations_users_trials?.map(org => (
          <Organization
            organization={org}
            key={org.organization?.organization_id}
          />
        ))}
      </OrgsContainer>
    </PageContainer>
  )
}

const Organization: React.FC<{ organization?: OrganizationUsersTrials }> = ({
  organization,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true)

  const sortedUsers = useMemo(() => {
    return organization?.users_trials?.sort((usr1, usr2) => {
      const usr1Email = usr1.user?.primary_email_address || ""
      const usr2Email = usr2.user?.primary_email_address || ""
      return usr1Email.localeCompare(usr2Email)
    })
  }, [organization?.users_trials])

  const userCount = organization?.users_trials?.length
  const orgName = organization?.organization?.organization_name
  const prefix = "organizations/"
  const parsedOrgName = orgName?.substring(prefix.length)

  return (
    <OrgContainer>
      <TitleRow onClick={() => setIsCollapsed(prev => !prev)}>
        {isCollapsed ? <KeyboardArrowDownStyled /> : <KeyboardArrowUpStyled />}
        <Typography
          variant="h3"
          sx={{
            marginLeft: "4px",
            color: extraColors.white,
          }}
        >
          {parsedOrgName}
        </Typography>
        <Typography
          variant="body1Bold"
          sx={{
            marginLeft: "12px",
            color: extraColors.white,
            opacity: 0.6,
          }}
        >
          {`(${userCount?.toString() ?? "0"} user${userCount === 1 ? "" : "s"})`}
        </Typography>
      </TitleRow>
      {!isCollapsed && (
        <UsersContainer>
          {sortedUsers?.map(user => (
            <UserWithTrial userTrial={user} key={user.user?.user_id} />
          ))}
        </UsersContainer>
      )}
    </OrgContainer>
  )
}
